/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GestaoTypes } from 'store/modules/bidding_store/types';
import { createCard } from 'services/actions/ai_management_cards';
import { MANAGE_MENU_ITEMS, ManageMenuItem } from 'services/ai_management_manage_menu';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';
import createTrack from 'services/actions/tracker_actions';
import {
  AiManagementCardModalAction,
  useAiManagementCardModal,
} from 'hooks/useAiManagementCardModal';
import { Container } from './styles';

const MODAL_ACTION_BY_MENU_ACTION: Record<
  NonNullable<ManageMenuItem['action']>,
  AiManagementCardModalAction | undefined
> = {
  card_modal: undefined,
  card_modal_new_task: 'new_task',
  card_modal_new_update: 'new_update',
};

interface BiddingManageButtonProps {
  biddingId: string;
  source: string;
  gestao?: GestaoTypes;
}

const BiddingManageButton: React.FC<BiddingManageButtonProps> = (props) => {
  const { biddingId, source, gestao } = props;

  const [managed, setManaged] = useState<boolean>(gestao?.managing ?? false);
  const [members, setMembers] = useState<Array<string>>(gestao?.members ?? []);
  const [count, setCount] = useState<number>(gestao?.members?.length ?? 0);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const cardModal = useAiManagementCardModal();
  const openingCard = cardModal.opening !== null;
  const menuRef = useRef<HTMLDivElement>(null);

  const managing = gestao?.managing;
  const membersKey = gestao?.members?.join('|') ?? '';

  useEffect(() => {
    if (managing === undefined) return;
    setManaged(managing);
    setMembers(gestao?.members ?? []);
    setCount(gestao?.members?.length ?? 0);
  }, [managing, membersKey]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleManage = () => {
    if (loading) return;

    const previousManaged = managed;
    const previousCount = count;
    setManaged(true);
    setCount(Math.max(count, 1));
    setLoading(true);

    createCard(Number(biddingId)).then((res) => {
      setLoading(false);
      if (!res || (res.status >= 400 && res.status !== 422)) {
        setManaged(previousManaged);
        setCount(previousCount);
        setMenuOpen(false);
        return;
      }

      createTrack('Gestão IA - Incluiu licitação', { fonte: source });
    });
  };

  const handleMainClick = () => {
    if (managed) {
      setMenuOpen((open) => !open);
      return;
    }
    handleManage();
  };

  const openCardModal = (action?: AiManagementCardModalAction) => {
    setMenuOpen(false);
    cardModal.openCard({ biddingId: Number(biddingId) }, action).then((found) => {
      if (found) createTrack('Gestão IA - Abriu a licitação pelo card', { fonte: source });
    });
  };

  const renderMenuItem = (item: ManageMenuItem) => {
    if (item.route) {
      return (
        <Link
          key={item.key}
          to={item.route}
          className="dropdown-item"
          onClick={() => {
            setMenuOpen(false);
            createTrack('Gestão IA - Acessou pelo card da licitação', { fonte: source });
          }}
        >
          {item.label}
        </Link>
      );
    }

    if (item.action) {
      const modalAction = MODAL_ACTION_BY_MENU_ACTION[item.action];
      return (
        <button
          key={item.key}
          type="button"
          className="dropdown-item"
          disabled={openingCard}
          onClick={() => openCardModal(modalAction)}
        >
          {item.label}
          {openingCard && <span className="spinner-border spinner-border-sm ml-2" role="status" />}
        </button>
      );
    }

    return (
      <span key={item.key} className="dropdown-item disabled" title="Em breve">
        {item.label}
      </span>
    );
  };

  return (
    <Container managed={managed} className="dropdown mr-2 d-print-none" ref={menuRef}>
      <div className="manage-box d-flex align-items-center rounded">
        <button
          type="button"
          className="manage-main btn btn-sm d-flex align-items-center text-white text-nowrap"
          aria-expanded={menuOpen}
          onClick={handleMainClick}
        >
          <i className="material-icons manage-star mr-1">star</i>
          {managed ? 'Gerenciando' : 'Gerenciar'}
          {count > 0 && (
            <span
              className="manage-count badge badge-light d-flex align-items-center ml-2"
              title={members.join(', ')}
            >
              <i className="material-icons">person</i>
              {count}
            </span>
          )}
        </button>

        {managed && (
          <button
            type="button"
            className="manage-caret btn btn-sm d-flex align-items-center text-white"
            aria-label="Opções do Gestão IA"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <i className="material-icons">expand_more</i>
          </button>
        )}
      </div>

      {menuOpen && (
        <div className="dropdown-menu show manage-menu">
          {MANAGE_MENU_ITEMS.map(renderMenuItem)}
        </div>
      )}

      {cardModal.card && (
        <AiManagementCardModal
          card={cardModal.card}
          initialAction={cardModal.action}
          onClose={cardModal.close}
        />
      )}
    </Container>
  );
};

export default BiddingManageButton;
